import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import api, { API_BASE, getImageUrl } from '../../utils/api'
import { toast } from 'react-toastify'

// Modular Step Components
import Step1BasicInfo from './project-steps/Step1BasicInfo';
import Step2Location from './project-steps/Step2Location';
import Step3Developer from './project-steps/Step3Developer';
import Step4Overview from './project-steps/Step4Overview';
import Step4FloorPlan from './project-steps/Step4FloorPlan';
import Step5SEO from './project-steps/Step5SEO';
import Step6Pricing from './project-steps/Step5Pricing';
import Step8Media from './project-steps/Step7Media';
import Step8Grid from './project-steps/Step8Grid';
import Step9Offers from './project-steps/Step9Offers';
import StepContactInfo from './project-steps/StepContactInfo';
import Step13SectionInfo from './project-steps/Step12SectionInfo';

// Shared Types & Constants
import { 
    ProjectForm, 
    FloorPlan, 
    InventoryItem, 
    Specification, 
    LegalDocument, 
    FAQ, 
    AdditionalDetail,
    NearbyPlace,
    PaymentPlan,
    GalleryCategory,
    GalleryState,
    GalleryFileState
} from './project-steps/types';
import { 
    STEPS, 
    NEARBY_CATEGORY_ICONS,
    SUBCATEGORIES,
    DEFAULT_CONSTRUCTION_STATUS_OPTIONS
} from './project-steps/constants';

// --- Interfaces ---


interface BuilderOffer {
    offerType: string
    example: string
    priceImpact: string
    startDate: string
    tillDate: string
}

export default function AdminEditProject() {
    const { id } = useParams()
    const { token } = useAuth()
    const navigate = useNavigate()
    const [currentStep, setCurrentStep] = useState(1)
    const [activeStep4Tab, setActiveStep4Tab] = useState<'2d' | '3d' | null>('2d')
    const [isExistingGallery, setIsExistingGallery] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [loading, setLoading] = useState(true)
    const [errors, setErrors] = useState<Record<string, boolean>>({})
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [showNearbyModal, setShowNearbyModal] = useState(false);
    const [nearbyEntry, setNearbyEntry] = useState<NearbyPlace>({ category: 'Schools', name: '', distance: '' });
    const [existingGallery, setExistingGallery] = useState<GalleryState>({ exterior: [], interior: [], amenities: [], construction: [] });
    const [realProjectId, setRealProjectId] = useState<string>('');

    // Form Master State
    const [form, setForm] = useState<ProjectForm>({
        name: '', listingType: 'For Sale', propertyType: ['Residential'], subCategory: ['Flat/Apartment'], projectStatus: 'Under Construction', progressPercentage: 0, tagline: '', slug: '', isOurPick: false,
        location: '', address: '', city: '', state: 'Gujarat', country: 'India', pinCode: '', googleMapLocation: '', google3dViewLink: '', latitude: '', longitude: '',
        developerName: '', coDeveloper: '', developerLogo: '', developerWebsite: '', developerDescription: '', developerTagline: '', totalProjects: '0', projectsCompleted: '0', projectsOngoing: '0',
        projectsUpcoming: '0',
        projectsSince: '',
        aboutDeveloper: '',
        developerId: '',
        // Step 4
        overviewConfiguration: '', overviewSizeRange: '', overviewPriceRange: '', overviewAvgPricePerSqFt: '', overviewProjectArea: '', overviewTotalPlotArea: '', overviewOpenSpacePercentage: '', overviewTotalTowers: '', overviewTotalFloors: '', overviewTotalUnits: '', overviewFloors: '',
        overviewAmenitiesIcon: '',
        
        // Step 5
        towers: [],
        
        // Step 6
        launchDate: '', possessionDate: '', timelineStatus: '', constructionStatusScore: 0,
        reraNumber: '', reraAuthority: 'Gujarat Real Estate Regulatory Authority', reraLink: '', reraApproved: '',
        shortDescription: '', fullDescription: '', highlights: [], investmentPotential: '', videoUrl: '',
        brochure: null,
        brochureUrl: '',
        images: [],
        virtualTours: [{ title: '', link: '' }],
        youtubeVideos: [{ title: '', link: '' }],
        builderOffers: [{ offerType: '', example: '', priceImpact: '', startDate: '', tillDate: '' }],
        faq: [{ question: '', answer: '' }],
        metaTitle: '', metaDescription: '', metaKeywords: '', canonicalUrl: '', focusKeyword: '',
        contactNumber: '', contactPhone: '', whatsappNumber: '', contactEmail: '',
        disclaimer: '',

        paymentPlans: [{ title: 'Possession Linked', description: 'Pay 25% now, rest on possession' }],
        bankApprovals: [],
        builderTrustScore: 0,
        localityTrustScore: 0,
        loadingScore: 0,
        legalTrustScore: 0,
        amenitiesScore: 0,
        specificationsScore: 0,
        status: 'Pending',
        nearbyPlaces: [],
        dynamicSlots: [],
        amenityIcons: {},
        customAmenities: [],
        sectionInfo: {
            overview: '',
            amenities: '',
            specifications: '',
            location: '',
            floorPlans: '',
            gallery: '',
            developer: '',
            nearby: '',
            about: '',
            highlights: '',
            faq: '',
            reviews: ''
        },
        tpImage: '',
        tpImageUrl: '',
        featuredImage: ''
    })

    const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false)

    // Complex Dynamic Fields
    const [additionalDetails, setAdditionalDetails] = useState<AdditionalDetail[]>([{ label: '', value: '', icon: '' }])
    const [floorPlans, setFloorPlans] = useState<FloorPlan[]>([{
        unitType: '',
        subUnits: [{ subUnitType: '', subUnitVariant: '', price: '', facing: [], reraArea: '', carpetArea: '', builtUpArea: '', superBuiltUpArea: '', details: [{ label: '', value: '' }], image: '', imagePreview: '', image2D: '', image2DPreview: '', image3D: '', image3DPreview: '', infoImage: '', infoImagePreview: '' }]
    }])
    const [inventory, setInventory] = useState<InventoryItem[]>([{ tower: '', floor: '', unitNo: '', unitType: '', configuration: '', size: '', price: '', status: 'Available' }])
    const [specifications, setSpecifications] = useState<Specification[]>([])
    const [selectedAmenities, setSelectedAmenities] = useState<string[]>([])
    const [globalAmenities, setGlobalAmenities] = useState<{ _id: string, name: string, icon: string, score: number }[]>([])
    const [globalSpecs, setGlobalSpecs] = useState<{ _id: string, name: string, icon: string, details: string }[]>([])
    const [globalDetails, setGlobalDetails] = useState<{ _id: string, label: string, icon: string }[]>([])
    const [legalDocuments, setLegalDocuments] = useState<LegalDocument[]>([{ title: '', image: '', imagePreview: '' }])
    const [faqs, setFaqs] = useState<FAQ[]>([{ question: '', answer: '' }])
    const [developers, setDevelopers] = useState<{
        _id: string, name: string, logo?: string, website?: string, totalProjects?: string, projectsCompleted?: string, projectsOngoing?: string, projectsUpcoming?: string;
        projectsSince?: string;
        deliveryRecord?: string;
    }[]>([]);
    const [cities, setCities] = useState<{ _id: string, name: string }[]>([])
    const [allLocalities, setAllLocalities] = useState<{ _id: string, name: string, city: any }[]>([])
    const [filteredLocalities, setFilteredLocalities] = useState<{ _id: string, name: string }[]>([])
    const [galleryPreviews, setGalleryPreviews] = useState<GalleryState>({
        exterior: [], interior: [], amenities: [], construction: []
    })
    const [galleryIcons, setGalleryIcons] = useState<Record<string, string>>({})
    const [globalLandmarkCategories, setGlobalLandmarkCategories] = useState<{ _id: string, name: string, icon: string }[]>([])
    const [scoreSettings, setScoreSettings] = useState<any>(null);

    // Media State
    const [featuredImage, setFeaturedImage] = useState<File | null>(null)
    const [featuredPreview, setFeaturedPreview] = useState('')
    const [tpImage, setTpImage] = useState<File | null>(null)
    const [tpPreview, setTpPreview] = useState('')
    const [gallery, setGallery] = useState<GalleryFileState>({
        exterior: [], interior: [], amenities: [], construction: []
    })
    const [brochureFile, setBrochureFile] = useState<File | null>(null)
    const [projectVideoFile, setProjectVideoFile] = useState<File | null>(null)
    const [constructionStatusImage, setConstructionStatusImage] = useState<File | null>(null)
    const [constructionStatusPreview, setConstructionStatusPreview] = useState('')
    const [seoImageFile, setSeoImageFile] = useState<File | null>(null)
    const [seoImagePreview, setSeoImagePreview] = useState('')

    const updateGalleryIcon = async (album: string, icon: File | string) => {
        if (typeof icon === 'string') {
            setGalleryIcons(prev => ({ ...prev, [album]: icon }));
        } else {
            try {
                // We need handleUpload here. In Edit project it might be different.
                // Checking how it's done in edit project.
                const urls = await handleUpload([icon]);
                if (urls[0]) {
                    setGalleryIcons(prev => ({ ...prev, [album]: urls[0] }));
                }
            } catch (err) {
                toast.error("Failed to upload icon");
            }
        }
    };

    const removeGalleryCategory = (album: string) => {
        const newExGallery = { ...existingGallery };
        delete newExGallery[album];
        setExistingGallery(newExGallery);

        const newGallery = { ...gallery };
        delete newGallery[album];
        setGallery(newGallery);

        const newPreviews = { ...galleryPreviews };
        delete newPreviews[album];
        setGalleryPreviews(newPreviews);

        const newIcons = { ...galleryIcons };
        delete newIcons[album];
        setGalleryIcons(newIcons);
    };

    const handleGalleryUpload = async (category: string, files: File[]) => {
        setGallery(prev => ({ ...prev, [category]: [...(prev[category] || []), ...files] }));
        const newPreviews = files.map(f => URL.createObjectURL(f));
        setGalleryPreviews(prev => ({ ...prev, [category]: [...(prev[category] || []), ...newPreviews] }));
    };

    const removeGalleryImage = (category: string, idx: number) => {
        const existingCount = (existingGallery[category] || []).length;
        if (idx < existingCount) {
            setExistingGallery(prev => ({ ...prev, [category]: prev[category].filter((_: string, i: number) => i !== idx) }));
            setGalleryPreviews(prev => ({ ...prev, [category]: prev[category].filter((_: string, i: number) => i !== idx) }));
        } else {
            const adjustedIdx = idx - existingCount;
            setGallery(prev => ({ ...prev, [category]: (prev[category] || []).filter((_: File, i: number) => i !== adjustedIdx) }));
            setGalleryPreviews(prev => ({ ...prev, [category]: (prev[category] || []).filter((_: string, i: number) => i !== idx) }));
        }
    };

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [amensRes, specsRes, devsRes, citiesRes, locsRes, scoreSettingsRes] = await Promise.all([
                    fetch(`${API_BASE}/amenities`).then(r => r.json()),
                    fetch(`${API_BASE}/specifications`).then(r => r.json()),
                    fetch(`${API_BASE}/developers`).then(r => r.json()),
                    fetch(`${API_BASE}/cities`).then(r => r.json()),
                    fetch(`${API_BASE}/localities`).then(r => r.json()),
                    fetch(`${API_BASE}/score-settings`).then(r => r.json()),
                ]);

                // Landmark categories fetch (isolated)
                api.getLandmarkCategories()
                    .then(data => {
                        if (Array.isArray(data)) {
                            console.log("Landmark categories fetched:", data);
                            setGlobalLandmarkCategories(data);
                        } else {
                            toast.error("Invalid format for landmark categories.");
                        }
                    })
                    .catch(err => {
                        console.error("Error fetching landmark categories:", err);
                        toast.error("Failed to sync landmark categories.");
                    });
                if (Array.isArray(amensRes)) setGlobalAmenities(amensRes);
                if (Array.isArray(specsRes)) setGlobalSpecs(specsRes);
                if (Array.isArray(devsRes)) setDevelopers(devsRes);
                if (Array.isArray(citiesRes)) setCities(citiesRes.filter((c: any) => c.isActive));
                if (Array.isArray(locsRes)) setAllLocalities(locsRes.filter((l: any) => l.isActive));
                
                if (scoreSettingsRes && !scoreSettingsRes.message) {
                    setScoreSettings(scoreSettingsRes);
                }

                if (id) {
                    const p = await fetch(`${API_BASE}/projects/${id}`).then(r => r.json());
                    if (p && !p.message) {
                        if (p._id) setRealProjectId(String(p._id));
                        const projectData = {
                            ...p,
                            name: p.name || '',
                            listingType: p.listingType || 'Sale',
                            propertyType: Array.isArray(p.propertyType) ? p.propertyType : (p.propertyType ? [p.propertyType] : []),
                            subCategory: Array.isArray(p.subCategory) ? p.subCategory : (p.subCategory ? [p.subCategory] : []),
                            projectStatus: p.projectStatus || 'New Launch',
                            progressPercentage: p.progressPercentage || 0,
                            tagline: p.tagline || '',
                            
                            location: p.location || '',
                            address: p.address || '',
                            city: p.city || '',
                            state: p.state || 'Gujarat',
                            country: p.country || 'India',
                            pinCode: p.pinCode || '',
                            googleMapLocation: p.googleMapLocation || p.mapDetails?.googleMapLocation || '',
                            google3dViewLink: p.google3dViewLink || p.mapDetails?.google3dViewLink || '',
                            latitude: p.latitude || p.mapDetails?.latitude || '',
                            longitude: p.longitude || p.mapDetails?.longitude || '',
                            
                            developerId: p.developer?._id || p.developer || '',
                            developerName: p.developerDetails?.name || p.developer?.name || p.developerName || '',
                            coDeveloper: p.developerDetails?.coDeveloper || p.developer?.coDeveloper || p.coDeveloper || '',
                            developerLogo: p.developerDetails?.logo || p.developer?.logo || p.developerLogo || '',
                            developerWebsite: p.developerDetails?.website || p.developer?.website || p.developerWebsite || '',
                            developerDescription: p.developerDetails?.description || p.developer?.description || p.developerDescription || '',
                            developerTagline: p.developerDetails?.tagline || p.developer?.tagline || p.developerTagline || '',
                            totalProjects: p.developerDetails?.totalProjects || p.developer?.totalProjects || p.totalProjects || '0',
                            projectsCompleted: p.developerDetails?.projectsCompleted || p.developer?.projectsCompleted || p.projectsCompleted || '0',
                            projectsOngoing: p.developerDetails?.projectsOngoing || p.developer?.projectsOngoing || p.projectsOngoing || '0',
                            projectsUpcoming: p.developerDetails?.projectsUpcoming || p.developer?.projectsUpcoming || p.projectsUpcoming || '0',
                            projectsSince: p.developerDetails?.projectsSince || p.developer?.projectsSince || p.projectsSince || '',
                            aboutDeveloper: p.developerDetails?.aboutDeveloper || p.developer?.aboutDeveloper || p.aboutDeveloper || '',
                            
                            overviewConfiguration: p.overview?.configuration || p.overviewConfiguration || '',
                            overviewSizeRange: p.overview?.sizeRange || p.overviewSizeRange || '',
                            overviewPriceRange: p.overview?.priceRange || p.overviewPriceRange || '',
                            overviewAvgPricePerSqFt: p.overview?.avgPricePerSqFt || p.overviewAvgPricePerSqFt || '',
                            overviewProjectArea: p.overview?.projectArea || p.overviewProjectArea || '',
                            overviewTotalPlotArea: p.overview?.totalPlotArea || p.overviewTotalPlotArea || '',
                            overviewOpenSpacePercentage: p.overview?.openSpacePercentage || p.overviewOpenSpacePercentage || '',
                            overviewTotalTowers: p.overview?.totalTowers || p.overviewTotalTowers || '',
                            overviewTotalFloors: p.overview?.totalFloors || p.overviewTotalFloors || '',
                            overviewTotalUnits: p.overview?.totalUnits || p.overviewTotalUnits || '',
                            overviewFloors: p.overview?.floors || p.overviewFloors || '',
                            overviewBathrooms: p.overview?.bathrooms || p.overviewBathrooms || '',
                            overviewAgeOfConstruction: p.overview?.ageOfConstruction || p.overviewAgeOfConstruction || '',
                            overviewParking: p.overview?.parking || p.overviewParking || '',
                            overviewAmenitiesIcon: p.overview?.amenitiesIcon || p.overviewAmenitiesIcon || '',
                            
                            launchDate: p.launchDate ? new Date(p.launchDate).toISOString().split('T')[0] : '',
                            possessionDate: p.possessionDate ? new Date(p.possessionDate).toISOString().split('T')[0] : '',
                            timelineStatus: p.constructionStatus || p.timelineStatus || '',
                            fullDescription: p.description || p.fullDescription || '',
                            highlights: p.highlights || [],
                            faq: p.faqs || p.faq || [],
                            nearbyPlaces: p.nearbyPlaces || [],
                            builderOffers: p.builderOffers?.map((o: any) => ({
                                offerType: o.offerType || '',
                                example: o.example || '',
                                priceImpact: o.priceImpact || '',
                                startDate: o.startDate ? new Date(o.startDate).toISOString().split('T')[0] : '',
                                tillDate: o.tillDate ? new Date(o.tillDate).toISOString().split('T')[0] : ''
                            })) || [{ offerType: '', example: '', priceImpact: '', startDate: '', tillDate: '' }],

                            amenityIcons: p.amenityIcons || {},
                            customAmenities: p.customAmenities || [],
                            paymentPlans: p.paymentPlans || [],
                            reraNumber: p.reraNumber || '',
                            reraApproved: p.reraApproved || (p.reraNumber ? 'Yes' : 'No'),
                            reraAuthority: p.reraAuthority || 'Gujarat Real Estate Regulatory Authority',
                            reraLink: p.reraLink || '',
                            virtualTours: p.virtualTours?.length > 0 ? p.virtualTours : [{ title: '', link: '' }],
                            youtubeVideos: p.youtubeVideos?.length > 0 ? p.youtubeVideos : [{ title: '', link: '' }],
                            tpImage: p.tpImage || '',
                            shortDescription: p.shortDescription || '',
                            metaTitle: p.metaTitle || '',
                            metaKeywords: p.metaKeywords || '',
                            metaDescription: p.metaDescription || '',
                            canonicalUrl: p.canonicalUrl || '',
                            focusKeyword: p.focusKeyword || '',
                            seoImageUrl: p.seoImageUrl || '',
                            sectionInfo: p.sectionInfo || {
                                overview: '',
                                amenities: '',
                                specifications: '',
                                location: '',
                                floorPlans: '',
                                gallery: '',
                                developer: '',
                                nearby: '',
                                about: '',
                                highlights: '',
                                faq: '',
                                reviews: ''
                            }
                        };

                        setForm(projectData);
                        setIsSlugManuallyEdited(true);

                        if (p.additionalDetails?.length > 0) setAdditionalDetails(p.additionalDetails);
                        if (p.inventory?.length > 0) setInventory(p.inventory);
                        if (p.specifications) setSpecifications(p.specifications);
                        if (p.amenities) setSelectedAmenities(p.amenities.map((a: any) => typeof a === 'string' ? a : a._id));
                        
                        // Populate filtered localities based on project city
                        if (p.city && Array.isArray(locsRes)) {
                            const filtered = locsRes.filter((l: any) => {
                                const cityObj = l.city;
                                const cityMatch = (cityObj && typeof cityObj === 'object') ? cityObj.name === p.city : cityObj === p.city;
                                return cityMatch;
                            });
                            setFilteredLocalities(filtered);
                        }

                        if (p.floorPlans?.length > 0) setFloorPlans(p.floorPlans.map((fp: any) => ({
                            ...fp,
                            subUnits: (fp.subUnits || []).map((su: any) => ({
                                ...su,
                                imagePreview: su.image ? getImageUrl(su.image) : '',
                                image2DPreview: su.image2D ? getImageUrl(su.image2D) : '',
                                image3DPreview: su.image3D ? getImageUrl(su.image3D) : '',
                                infoImagePreview: su.infoImage ? getImageUrl(su.infoImage) : '',
                            }))
                        })));

                        if (p.featuredImage) setFeaturedPreview(getImageUrl(p.featuredImage));
                        if (p.tpImage) setTpPreview(getImageUrl(p.tpImage));
                        if (p.seoImageUrl) setSeoImagePreview(getImageUrl(p.seoImageUrl));

                        if (p.gallery) {
                            const eg: GalleryState = {
                                exterior: p.gallery.exterior || [],
                                interior: p.gallery.interior || [],
                                amenities: p.gallery.amenities || [],
                                construction: p.gallery.construction || [],
                            };
                            // Add any other categories from the gallery object
                            Object.keys(p.gallery).forEach(key => {
                                if (!['exterior', 'interior', 'amenities', 'construction'].includes(key)) {
                                    eg[key] = p.gallery[key] || [];
                                }
                            });
                            
                            setExistingGallery(eg);
                            const previews: GalleryState = { exterior: [], interior: [], amenities: [], construction: [] };
                            Object.keys(eg).forEach(key => {
                                previews[key] = eg[key].map((u: string) => getImageUrl(u));
                            });
                            setGalleryPreviews(previews);
                            if (p.galleryIcons) {
                                const icons: Record<string, string> = {};
                                Object.keys(p.galleryIcons).forEach(k => {
                                    icons[k] = p.galleryIcons[k].startsWith('http') ? getImageUrl(p.galleryIcons[k]) : p.galleryIcons[k];
                                });
                                setGalleryIcons(icons);
                            }
                        }
                        if (p.legalDocuments) setLegalDocuments(p.legalDocuments.map((ld: any) => ({ ...ld, imagePreview: ld.image ? getImageUrl(ld.image) : '' })));
                        if (p.faqs?.length > 0) setFaqs(p.faqs);
                    }
                }
            } catch (err) {
                console.error(err);
                toast.error('Failed to load project data');
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [id])

    useEffect(() => {
        if (isSlugManuallyEdited || !form.name) return;
        const slugify = (text: string) => {
            return text
                .toString()
                .toLowerCase()
                .trim()
                .replace(/\s+/g, '-')
                .replace(/[^\w\-]+/g, '')
                .replace(/\-\-+/g, '-')
                .replace(/^-+|-+$/g, '');
        };

        const namePart = slugify(form.name);
        const locationPart = form.location ? slugify(form.location) : '';
        const cityPart = form.city ? slugify(form.city) : '';

        const parts = [namePart, locationPart, cityPart].filter(Boolean);
        const newSlug = parts.join('-');

        setForm(prev => {
            if (prev.slug === newSlug) return prev;
            return { ...prev, slug: newSlug };
        });
    }, [form.name, form.location, form.city, isSlugManuallyEdited]);


    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

        if (name === 'propertyType') {
            const valStr = value as string;
            setForm(p => {
                const newTypes = p.propertyType.includes(valStr) 
                    ? p.propertyType.filter(t => t !== valStr) 
                    : [...p.propertyType, valStr];
                
                // Keep subcategories that are still valid for the selected property types
                const validSubs = new Set<string>();
                newTypes.forEach(t => {
                    (SUBCATEGORIES[t] || []).forEach(s => validSubs.add(s));
                });
                const newSubs = p.subCategory.filter(s => validSubs.has(s));
                
                return { ...p, propertyType: newTypes, subCategory: newSubs };
            });
        } else if (name === 'subCategory') {
            const valStr = value as string;
            setForm(p => {
                const newSubs = p.subCategory.includes(valStr)
                    ? p.subCategory.filter(s => s !== valStr)
                    : [...p.subCategory, valStr];
                return { ...p, subCategory: newSubs };
            });
        } else if (name === 'developerId') {
            const dev = developers.find(d => d._id === value);
            setForm(p => ({
                ...p,
                developerId: value,
                developerName: dev?.name || '',
                developerLogo: dev?.logo || '',
                developerWebsite: dev?.website || '',
                totalProjects: dev?.totalProjects || '0',
                projectsSince: dev?.projectsSince || ''
            }))
        } else if (name === 'city') {
            const cityName = value;
            setForm(p => ({ ...p, city: cityName, location: '' }));
            // Populate filtered localities based on selected city
            const filtered = allLocalities.filter((l: any) => {
                const cityObj = l.city;
                const cityMatch = (cityObj && typeof cityObj === 'object') ? cityObj.name === cityName : cityObj === cityName;
                return cityMatch;
            });
            setFilteredLocalities(filtered);
        } else if (name === 'timelineStatus') {
            const options = scoreSettings?.constructionStatusOptions?.length > 0 
                ? scoreSettings.constructionStatusOptions 
                : DEFAULT_CONSTRUCTION_STATUS_OPTIONS;
            const option = options.find((o: any) => o.label === value);
            
            // Logic for Required Documents in Step 5
            const getRequiredTitles = (status: string) => {
                if (status === 'RERA Approved') return ["Title Deed"];
                if (status === 'Under Construction' || status === 'Near Possession') return ["RERA Certificate", "Title Deed", "Approved Plan"];
                if (status === 'Ready to Move') return ["RERA Certificate", "Title Deed", "Approved Plan", "Occupancy Certificate (OC)"];
                return [];
            };

            const titles = getRequiredTitles(value);
            // Pre-fill legalDocuments state with these titles if they don't exist
            setLegalDocuments(prev => {
                const existing = [...prev];
                return titles.map(t => {
                    const found = existing.find(ex => ex.title === t);
                    return found ? found : { title: t, image: '', imagePreview: '', link: '' };
                });
            });

            setForm(p => ({
                ...p,
                timelineStatus: value,
                constructionStatusScore: option ? option.score : 0
            }));
        } else if (name === 'slug') {
            setIsSlugManuallyEdited(true);
            setForm(p => ({ ...p, slug: value }));
        } else if (name === 'name') {
            setForm(p => ({ ...p, name: value }));
        } else {
            setForm(p => ({ ...p, [name]: val }))
        }
    }

    const nextStep = () => setCurrentStep(s => Math.min(s + 1, 11))
    const prevStep = () => setCurrentStep(s => Math.max(s - 1, 1))

    const QuickUpdateButton = () => (
        <button 
            type="button" 
            onClick={(e) => handleSubmit(e, undefined, false)}
            disabled={submitting} 
            className="px-4 py-2 bg-yellow-400 text-blue-950 border border-yellow-500 rounded-xl font-bold text-xs shadow-sm active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50"
        >
            {submitting ? <i className="fa-solid fa-circle-notch animate-spin text-[10px]"></i> : <i className="fa-solid fa-cloud-arrow-up text-[10px]"></i>}
            {submitting ? 'Saving...' : 'Quick Update'}
        </button>
    );

    const handleUpload = async (files: File[]) => {
        const effectiveToken = token || localStorage.getItem('resido_token') || localStorage.getItem('token') || '';
        if (!effectiveToken || files.length === 0) return [];
        try {
            const formData = new FormData();
            files.forEach(file => formData.append('images', file));
            
            const res = await fetch(`${API_BASE}/upload`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${effectiveToken}` },
                body: formData
            });
            
            const data = await res.json();
            
            if (!res.ok) {
                toast.error(`Upload failed: ${data.message || 'Server error'}`);
                return [];
            }
            
            return data.urls || [];
        } catch (err) {
            console.error('Upload failed', err);
            toast.error('Connection error during upload. Check file size.');
            return [];
        }
    };

    const handleSubmit = async (e?: React.FormEvent, statusOverride?: string, silent?: boolean) => {
        if (e) e.preventDefault();
        const effectiveToken = token || localStorage.getItem('resido_token') || localStorage.getItem('token') || '';
        if (!effectiveToken || !id) {
            toast.error('Please log in again to continue.');
            return false;
        }
        
        const isDraft = statusOverride === 'Draft';
        if (!silent) setSubmitting(true);
        
        try {
            // 1. Upload Featured Image
            let featuredUrl = form.featuredImage;
            if (featuredImage) {
                const urls = await handleUpload([featuredImage]);
                featuredUrl = urls[0] || featuredUrl;
            }

            // 1.5 Upload TP Image
            let tpUrl = form.tpImage;
            if (tpImage) {
                const urls = await handleUpload([tpImage]);
                tpUrl = urls[0] || tpUrl;
            }
            
            // 1.6 Upload Brochure
            let brochureUrl = form.brochureUrl || '';
            if (brochureFile) {
                const fd = new FormData();
                fd.append('document', brochureFile);
                const res = await fetch(`${API_BASE}/upload/document`, {
                    method: 'POST', headers: { Authorization: `Bearer ${effectiveToken}` }, body: fd
                });
                const data = await res.json();
                brochureUrl = data.url || '';
            }
            
            // 1.7 Upload Project Video
            let projectVideoUrl = form.projectVideoUrl || '';
            if (projectVideoFile) {
                const fd = new FormData();
                fd.append('document', projectVideoFile);
                const res = await fetch(`${API_BASE}/upload/document`, {
                    method: 'POST', headers: { Authorization: `Bearer ${effectiveToken}` }, body: fd
                });
                const data = await res.json();
                projectVideoUrl = data.url || '';
            }

            // 2. Upload Gallery
            const galleryUrls: Record<string, string[]> = {};
            const allCategories = Array.from(new Set([...Object.keys(existingGallery || {}), ...Object.keys(gallery || {})]));
            
            for (const category of allCategories) {
                const existing = (existingGallery && existingGallery[category]) || [];
                const newlyUploaded = (gallery && gallery[category]) ? await handleUpload(gallery[category]) : [];
                galleryUrls[category] = [...existing, ...newlyUploaded];
            }

            // 3. Update Floor Plans with uploads
            const updatedFloorPlans = await Promise.all((floorPlans || []).map(async fp => ({
                unitType: fp.unitType || '',
                subUnits: await Promise.all((fp.subUnits || []).map(async su => ({
                    ...su,
                    image: su.imageFile ? (await handleUpload([su.imageFile]))[0] : (su.image || ''),
                    image2D: su.image2DFile ? (await handleUpload([su.image2DFile]))[0] : (su.image2D || ''),
                    image3D: su.image3DFile ? (await handleUpload([su.image3DFile]))[0] : (su.image3D || ''),
                    infoImage: su.infoImageFile ? (await handleUpload([su.infoImageFile]))[0] : (su.infoImage || ''),
                    virtual3DLink: su.virtual3DLink || ''
                })))
            })));

            // 4. Update Legal Documents
            const updatedLegalDocs = await Promise.all((legalDocuments || []).filter(ld => ld && ld.title).map(async ld => ({
                title: ld.title,
                image: ld.file ? (await handleUpload([ld.file]))[0] : (ld.image || ''),
                link: ld.link || ''
            })));

            // 4.5 Upload SEO Image
            let seoImageUrlStr = form.seoImageUrl || '';
            if (seoImageFile) {
                const urls = await handleUpload([seoImageFile]);
                seoImageUrlStr = urls[0] || seoImageUrlStr;
            }

            // 4.6 Upload custom amenities icons
            const processedCustomAmenities = await Promise.all((form.customAmenities || []).map(async ca => {
                if (ca.file) {
                    const urls = await handleUpload([ca.file]);
                    return { name: ca.name, icon: urls[0] || ca.icon };
                }
                return { name: ca.name, icon: ca.icon };
            }));

            // 5. Construct Final Payload with Scoring Logic
            const payload = {
                ...form,
                amenityIcons: form.amenityIcons || {},
                customAmenities: processedCustomAmenities,
                status: statusOverride || form.status || 'Pending',
                developer: form.developerId || undefined,
                developerDetails: {
                    name: form.developerName || '',
                    coDeveloper: form.coDeveloper || '',
                    logo: form.developerLogo || '',
                    website: form.developerWebsite || '',
                    description: form.developerDescription || '',
                    tagline: form.developerTagline || '',
                    totalProjects: form.totalProjects || '0',
                    projectsCompleted: form.projectsCompleted || '0',
                    projectsOngoing: form.projectsOngoing || '0',
                    projectsUpcoming: form.projectsUpcoming || '0',
                    projectsSince: form.projectsSince || '',
                },
                mapDetails: {
                    googleMapLocation: form.googleMapLocation || '',
                    google3dViewLink: form.google3dViewLink || '',
                    latitude: form.latitude || '',
                    longitude: form.longitude || '',
                },
                virtualTours: (form.virtualTours || []).filter(vt => vt && vt.link),
                youtubeVideos: (form.youtubeVideos || []).filter(yv => yv && yv.link),
                overview: {
                    configuration: form.overviewConfiguration || '',
                    sizeRange: form.overviewSizeRange || '',
                    priceRange: form.overviewPriceRange || '',
                    avgPricePerSqFt: form.overviewAvgPricePerSqFt || '',
                    projectArea: form.overviewTotalPlotArea || form.overviewProjectArea || '',
                    totalPlotArea: form.overviewTotalPlotArea || '',
                    openSpacePercentage: form.overviewOpenSpacePercentage || '',
                    totalTowers: form.overviewTotalTowers || '',
                    totalFloors: form.overviewTotalFloors || '',
                    totalUnits: form.overviewTotalUnits || '',
                    floors: form.overviewFloors || '',
                    bathrooms: form.overviewBathrooms || '',
                    ageOfConstruction: form.overviewAgeOfConstruction || '',
                    parking: form.overviewParking || '',
                    amenitiesIcon: form.overviewAmenitiesIcon || ''
                },
                featuredImage: featuredUrl || '',
                tpImage: tpUrl || '',
                brochureUrl: brochureUrl || '',
                projectVideoUrl: projectVideoUrl || '',
                gallery: galleryUrls,
                galleryIcons: galleryIcons || {},
                floorPlans: updatedFloorPlans,
                legalDocuments: updatedLegalDocs,
                amenities: selectedAmenities || [],
                specifications: await Promise.all((specifications || []).map(async s => {
                    let iconUrl = s.icon;
                    if (s.file) {
                        const urls = await handleUpload([s.file]);
                        iconUrl = urls[0] || iconUrl;
                    }
                    return {
                        category: s.category || '',
                        details: s.details || '',
                        icon: iconUrl || ''
                    };
                })),
                highlights: await Promise.all((form.highlights || []).map(async h => {
                    let iconUrl = h.icon;
                    if (h.file) {
                        const urls = await handleUpload([h.file]);
                        iconUrl = urls[0] || iconUrl;
                    }
                    return { icon: iconUrl || '', text: h.text || '' };
                })),
                nearbyPlaces: form.nearbyPlaces || [],
                sectionInfo: form.sectionInfo || {},
                builderOffers: (form.builderOffers || []).filter(offer => offer && offer.offerType),
                additionalDetails: (additionalDetails || []).filter(d => d && d.label),
                faqs: (form.faq || []).filter(f => f && f.question),
                shortDescription: form.shortDescription || '',
                metaTitle: form.metaTitle || '',
                metaDescription: form.metaDescription || '',
                metaKeywords: form.metaKeywords || '',
                canonicalUrl: form.canonicalUrl || '',
                focusKeyword: form.focusKeyword || '',
                seoImageUrl: seoImageUrlStr || '',
                description: form.fullDescription || '',
                constructionStatus: form.timelineStatus || '',
                contactPhone: form.contactPhone || '',
                whatsappNumber: form.whatsappNumber || '',
                
                // --- Calculated Trust Scores ---
                builderTrustScore: (() => {
                    const dev = (developers || []).find(d => d._id === form.developerId);
                    if (!dev) return 0;
                    let s = 0;
                    const years = new Date().getFullYear() - parseInt(dev.projectsSince || '2026');
                    if (years >= 20) s += 5; else if (years >= 15) s += 4; else if (years >= 10) s += 3; else if (years >= 5) s += 2; else s += 1;
                    const comp = parseInt(dev.projectsCompleted || '0');
                    if (comp >= 50) s += 4; else if (comp >= 30) s += 3; else if (comp >= 15) s += 2; else if (comp >= 5) s += 1; else s += 0.5;
                    const ong = parseInt(dev.projectsOngoing || '0');
                    if (ong >= 20) s += 3; else if (ong >= 10) s += 2; else if (ong >= 5) s += 1; else s += 0.5;
                    if (dev.deliveryRecord === 'on-time') s += 2; else if (dev.deliveryRecord === 'minor-delay') s += 1;
                    const upc = parseInt(dev.projectsUpcoming || '0');
                    if (upc >= 10) s += 1; else if (upc >= 5) s += 0.7; else s += 0.5;
                    return s;
                })(),
                localityTrustScore: (() => {
                    let score = 0;
                    const rules = scoreSettings?.localityScoreRules || [];
                    const usedCategories = new Set();
                    
                    rules.forEach((rule: any) => {
                        if (!usedCategories.has(rule.category)) {
                            const hasMatch = (form.nearbyPlaces || []).some(place => {
                                const distance = parseFloat(place.distance) || 0;
                                return place.category === rule.category && distance > 0 && distance <= rule.maxDistanceKm;
                            });
                            
                            if (hasMatch) {
                                score += rule.points;
                                usedCategories.add(rule.category);
                            }
                        }
                    });
                    return score;
                })(),
                loadingScore: (() => {
                    const loadingScores = (floorPlans || []).flatMap(fp => fp.subUnits || []).filter(Boolean).map(su => {
                        const carpet = parseFloat(su.carpetArea);
                        const superB = parseFloat(su.superBuiltUpArea);
                        if (carpet && superB && superB > carpet) {
                            const pct = ((superB - carpet) / superB) * 100;
                            if (pct <= 30) return 5; if (pct <= 35) return 4; if (pct <= 40) return 3; if (pct <= 45) return 2; return 1;
                        }
                        return 0;
                    }).filter(s => s > 0);
                    return loadingScores.length > 0 ? parseFloat((loadingScores.reduce((a: number, b: number) => a + b, 0) / loadingScores.length).toFixed(1)) : 0;
                })(),
                legalTrustScore: (() => {
                    const rules = scoreSettings?.legalTrustRules || [];
                    let score = 0;
                    
                    rules.forEach((rule: any) => {
                        if (!rule.enabled) return;
                        
                        switch (rule.key) {
                            case 'timelineStatus':
                                if (form.timelineStatus) score += rule.points;
                                break;
                            case 'reraApproved':
                                if (form.reraApproved === 'Yes') score += rule.points;
                                break;
                            case 'launchDate':
                                if (form.launchDate) score += rule.points;
                                break;
                            case 'possessionDate':
                                if (form.possessionDate) score += rule.points;
                                break;
                        }
                    });
                    
                    return score;
                })(),
                amenitiesScore: (selectedAmenities || []).reduce((acc, id) => acc + (globalAmenities.find(a => a._id === id)?.score || 0), 0),
                specificationsScore: Math.min((specifications || []).length, 3)
            };

            delete (payload as any)._id;
            const targetId = realProjectId || (form as any)._id || id;

            const res = await fetch(`${API_BASE}/projects/${targetId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${effectiveToken}` },
                body: JSON.stringify(payload)
            });
            const responseData = await res.json();
            if (res.ok) {
                // 6. Synchronize Inventory
                if (inventory && inventory.length > 0) {
                    await api.bulkSyncInventory(effectiveToken, targetId, inventory);
                }

                if (!silent) {
                    toast.success(isDraft ? 'Draft updated successfully!' : 'Project updated successfully!');
                    if (!isDraft && currentStep === 11) {
                        setTimeout(() => navigate('/admin-projects'), 1500);
                    }
                }
                return true;
            } else {
                toast.error(responseData.message || responseData.error || 'Update failed');
                return false;
            }
        } catch (err: any) {
            console.error('Project Update Error:', err);
            toast.error(err.message || 'Server error occurred while updating project');
            return false;
        } finally {
            if (!silent) setSubmitting(false);
        }
    };

    const handleSaveDraft = () => {
        handleSubmit(undefined, 'Draft', false);
    };

    const handleNextWithSave = async () => {
        setSubmitting(true);
        try {
            const success = await handleSubmit(undefined, form.status || 'Draft', false);
            if (success) {
                nextStep();
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (err: any) {
            console.error("Save & Continue error:", err);
            toast.error(err.message || "Failed to proceed to next step");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="py-20 text-center">
                    <div className="w-12 h-12 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="font-bold text-[#05144b] uppercase tracking-widest text-xs">Loading Project Data...</p>
                </div>
            </AdminLayout>
        );
    }

    const handleResetAll = () => {
        if (window.confirm("ARE YOU SURE? THIS WILL RESONATE THROUGH ALL 11 STEPS AND CLEAR EVERY INPUT FIELD. THIS ACTION IS IRREVERSIBLE FOR LOCAL STATE.")) {
            setForm({
                name: '', listingType: 'For Sale', propertyType: ['Residential'], subCategory: ['Flat/Apartment'], projectStatus: 'Under Construction', progressPercentage: 0, tagline: '', slug: '', isOurPick: false,
                location: '', address: '', city: '', state: 'Gujarat', country: 'India', pinCode: '', googleMapLocation: '', google3dViewLink: '', latitude: '', longitude: '',
                developerName: '', coDeveloper: '', developerLogo: '', developerWebsite: '', developerDescription: '', developerTagline: '', totalProjects: '0', projectsCompleted: '0', projectsOngoing: '0',
                projectsUpcoming: '0', projectsSince: '', aboutDeveloper: '', developerId: '',
                overviewConfiguration: '', overviewSizeRange: '', overviewPriceRange: '', overviewAvgPricePerSqFt: '', overviewProjectArea: '', overviewTotalPlotArea: '', overviewOpenSpacePercentage: '', overviewTotalTowers: '', overviewTotalFloors: '', overviewTotalUnits: '', overviewFloors: '',
                overviewAmenitiesIcon: '', 
                towers: [],
                launchDate: '', possessionDate: '', timelineStatus: '', constructionStatusScore: 0,
                reraNumber: '', reraAuthority: 'Gujarat Real Estate Regulatory Authority', reraLink: '', reraApproved: '',
                shortDescription: '', fullDescription: '', highlights: [], investmentPotential: '', videoUrl: '',
                brochure: null, brochureUrl: '', images: [], virtualTours: [{ title: '', link: '' }], youtubeVideos: [{ title: '', link: '' }],
                builderOffers: [{ offerType: '', example: '', priceImpact: '', startDate: '', tillDate: '' }],
                faq: [{ question: '', answer: '' }],
                metaTitle: '', metaDescription: '', metaKeywords: '', canonicalUrl: '', focusKeyword: '',
                contactNumber: '', contactPhone: '', whatsappNumber: '', contactEmail: '', disclaimer: '',

                paymentPlans: [{ title: 'Possession Linked', description: 'Pay 25% now, rest on possession' }],
                bankApprovals: [], builderTrustScore: 0, localityTrustScore: 0, loadingScore: 0, legalTrustScore: 0, amenitiesScore: 0, specificationsScore: 0,
                status: 'Pending', nearbyPlaces: [], dynamicSlots: [],
                tpImage: '', tpImageUrl: '',
                featuredImage: '',
                sectionInfo: {
                    overview: '',
                    amenities: '',
                    specifications: '',
                    location: '',
                    floorPlans: '',
                    gallery: '',
                    developer: '',
                    nearby: '',
                    about: '',
                    highlights: '',
                    faq: '',
                    reviews: ''
                }
            });
            setAdditionalDetails([{ label: '', value: '', icon: '' }]);
            setFloorPlans([{ unitType: '', subUnits: [{ subUnitType: '', subUnitVariant: '', price: '', facing: [], carpetArea: '', builtUpArea: '', superBuiltUpArea: '', details: [{ label: '', value: '' }], image: '', imagePreview: '', image2D: '', image2DPreview: '', image3D: '', image3DPreview: '' }] }]);
            setSelectedAmenities([]);
            setSpecifications([]);
            setLegalDocuments([{ title: '', image: '', imagePreview: '' }]);
            setFaqs([{ question: '', answer: '' }]);
            setFeaturedImage(null);
            setFeaturedPreview('');
            setTpImage(null);
            setTpPreview('');
            setGallery({ exterior: [], interior: [], amenities: [], construction: [] });
            setGalleryPreviews({ exterior: [], interior: [], amenities: [], construction: [] });
            setExistingGallery({ exterior: [], interior: [], amenities: [], construction: [] });
            setBrochureFile(null);
            setConstructionStatusImage(null);
            setConstructionStatusPreview('');
            toast.info("ALL SYSTEMS CLEARED TO ZERO.");
            setCurrentStep(1);
        }
    };
    return (
        <AdminLayout>
            <div className="mb-2 disable-animations">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-2xl font-bold text-black mb-1">Edit Project</h3>
                        <p className="text-black text-[11px] font-bold">Update project details across all 11 sections.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={handleResetAll} type="button" className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-[#05144b] rounded-xl text-xs font-bold text-[#05144b] transition-all shadow-sm">
                            <i className="fa-solid fa-trash-can"></i> Clear All
                        </button>
                        <Link to="/admin-projects" className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-900/10 rounded-xl text-xs font-bold text-black transition-all">
                            <i className="fa-solid fa-arrow-left"></i> Back to Projects
                        </Link>
                    </div>
                </div>
            </div>

            {/* 10-Step Horizontal Progress Stepper */}
            <div className="bg-white border border-gray-150 rounded-2xl shadow-sm mb-8 overflow-hidden p-6">
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-3">
                         <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white font-extrabold shadow-md text-base">
                            {currentStep}
                        </div>
                        <div>
                            <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">Active Step {currentStep} of 11</span>
                            <h4 className="text-base font-extrabold text-gray-800 leading-tight">{STEPS[currentStep - 1]}</h4>
                        </div>
                    </div>
                    <div className="flex items-center gap-6">
                        <QuickUpdateButton />
                        <div className="text-right">
                            <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Completion Progress</span>
                            <div className="flex items-center gap-2 justify-end">
                                <span className="text-lg font-black text-blue-600">{Math.round((currentStep / 11) * 100)}%</span>
                                <span className="text-gray-400 text-xs font-bold">/ 100%</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Horizontal segment progress trackers */}
                <div className="flex items-center w-full mt-4 overflow-x-auto pb-4 hide-scrollbar">
                    {STEPS.map((stepName, index) => {
                        const stepId = index + 1;
                        const isSegActive = currentStep === stepId;
                        const isSegCompleted = currentStep > stepId;

                        let colorClass = 'text-gray-400 border-gray-200';
                        let circleClass = 'bg-gray-100 text-gray-500';
                        
                        if (isSegActive) {
                            colorClass = 'text-blue-600 border-blue-600';
                            circleClass = 'bg-blue-600 text-white shadow-md';
                        } else if (isSegCompleted) {
                            colorClass = 'text-[#1a9b55] border-[#1a9b55]';
                            circleClass = 'bg-[#1a9b55] text-white shadow-md';
                        }

                        return (
                            <div key={stepId} className="flex-1 min-w-[max-content] px-2 relative group cursor-pointer" onClick={() => setCurrentStep(stepId)}>
                                <div className={`flex items-center gap-2.5 pb-3 border-b-2 transition-all duration-300 ${colorClass}`}>
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center font-black text-xs transition-all ${circleClass}`}>
                                        {stepId}
                                    </div>
                                    <div className="text-left whitespace-nowrap">
                                        <span className="block text-[11px] font-black tracking-wide leading-tight">
                                            {stepName}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {msg && (
                <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${msg.type === 'success' ? 'bg-white border-2 border-[#05144b] text-[#05144b]' : 'bg-white border-2 border-rose-600 text-rose-600' }`}>
                    <i className={`fa-solid ${msg.type === 'success' ? 'fa-circle-check text-[#05144b]' : 'fa-circle-exclamation text-rose-600'} text-lg`}></i>
                    <span className="font-semibold text-sm">{msg.text}</span>
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="bg-white dark:bg-slate-800 border border-blue-900/10 dark:border-slate-700 rounded-xl px-2 md:px-4 py-6 md:py-8 shadow-sm mb-8">
                    {currentStep === 1 && (
                        <Step1BasicInfo 
                            form={form} 
                            setForm={setForm} 
                            handleChange={handleChange} 
                            errors={errors}
                            featuredImage={featuredImage}
                            setFeaturedImage={setFeaturedImage}
                            featuredPreview={featuredPreview}
                            setFeaturedPreview={setFeaturedPreview}
                            QuickUpdateButton={QuickUpdateButton} scoreSettings={scoreSettings}
                        />
                    )}
                    {currentStep === 2 && (
                        <Step2Location 
                            form={form} 
                            handleChange={handleChange} 
                            cities={cities} 
                            filteredLocalities={filteredLocalities} 
                            errors={errors} 
                            QuickUpdateButton={QuickUpdateButton} scoreSettings={scoreSettings}
                        />
                    )}
                    {currentStep === 3 && (
                        <Step3Developer 
                            form={form} 
                            setForm={setForm} 
                            handleChange={handleChange} 
                            developers={developers} 
                            setDevelopers={setDevelopers}
                            errors={errors} 
                            showDeveloperOnly={true} 
                            QuickUpdateButton={QuickUpdateButton} scoreSettings={scoreSettings}
                        />
                    )}
                    {currentStep === 4 && (
                        <Step4Overview 
                            form={form} 
                            setForm={setForm}
                            handleChange={handleChange} 
                            errors={errors} 
                            QuickUpdateButton={QuickUpdateButton} scoreSettings={scoreSettings}
                        />
                    )}
                    {currentStep === 5 && (
                        <div className="space-y-10">
                            <Step5SEO 
                                form={form} 
                                handleChange={handleChange} 
                                errors={errors} 
                                seoImageFile={seoImageFile}
                                setSeoImageFile={setSeoImageFile}
                                seoImagePreview={seoImagePreview}
                                setSeoImagePreview={setSeoImagePreview}
                                QuickUpdateButton={QuickUpdateButton} scoreSettings={scoreSettings}
                            />
                        </div>
                    )}
                    {currentStep === 6 && (
                        <Step4FloorPlan 
                            activeStep4Tab={activeStep4Tab}
                            setActiveStep4Tab={setActiveStep4Tab}
                            floorPlans={floorPlans}
                            setFloorPlans={setFloorPlans}
                            getImageUrl={getImageUrl}
                            errors={errors}
                            form={form}
                            QuickUpdateButton={QuickUpdateButton} scoreSettings={scoreSettings}
                        />
                    )}
                    {currentStep === 7 && (
                        <Step8Media 
                            featuredPreview={featuredPreview}
                            setFeaturedImage={setFeaturedImage}
                            setFeaturedPreview={setFeaturedPreview}
                            tpPreview={tpPreview}
                            setTpImage={setTpImage}
                            setTpPreview={setTpPreview}
                            galleryPreviews={galleryPreviews}
                            galleryIcons={galleryIcons}
                            handleGalleryUpload={handleGalleryUpload}
                            updateGalleryIcon={updateGalleryIcon}
                            removeGalleryImage={removeGalleryImage}
                            removeGalleryCategory={removeGalleryCategory}
                            brochureFile={brochureFile}
                            setBrochureFile={setBrochureFile}
                            form={form}
                            setForm={setForm}
                            handleChange={handleChange}
                            errors={errors}
                            QuickUpdateButton={QuickUpdateButton} scoreSettings={scoreSettings}
                        />
                    )}
                    {currentStep === 8 && (
                        <Step8Grid
                            form={form}
                            setForm={setForm}
                            handleChange={handleChange}
                            errors={errors}
                            selectedAmenities={selectedAmenities}
                            setSelectedAmenities={setSelectedAmenities}
                            globalAmenities={globalAmenities}
                            specifications={specifications}
                            setSpecifications={setSpecifications}
                            legalDocuments={legalDocuments}
                            setLegalDocuments={setLegalDocuments}
                            globalLandmarkCategories={globalLandmarkCategories}
                            scoreSettings={scoreSettings}
                        />
                    )}
                    {currentStep === 9 && (
                        <div className="space-y-10">
                            <Step9Offers 
                                form={form} 
                                setForm={setForm} 
                                errors={errors} 
                                QuickUpdateButton={QuickUpdateButton} scoreSettings={scoreSettings}
                            />
                        </div>
                    )}
                    {currentStep === 10 && (
                        <div className="space-y-10">
                            <StepContactInfo 
                                form={form} 
                                setForm={setForm} 
                                handleChange={handleChange} 
                                errors={errors}
                            />
                        </div>
                    )}
                    {currentStep === 11 && (
                        <div className="space-y-10">
                            <Step13SectionInfo 
                                form={form} 
                                setForm={setForm} 
                                handleChange={handleChange}
                                QuickUpdateButton={QuickUpdateButton} scoreSettings={scoreSettings}
                            />
                        </div>
                    )}
                </div>

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between gap-4 mt-8 mb-20 bg-slate-50 p-5 rounded-xl border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className="px-5 py-2.5 rounded-lg bg-white border border-gray-200 text-slate-700 font-semibold text-sm flex items-center gap-2 transition-all hover:bg-gray-50 active:scale-95 shadow-sm"
                            onClick={() => navigate('/admin-projects')}
                        >
                            <i className="fa-solid fa-arrow-left text-xs"></i>
                            Back to List
                        </button>
                        {currentStep > 1 && (
                            <button
                                type="button"
                                className="px-5 py-2.5 rounded-lg bg-white border border-slate-300 text-slate-700 font-semibold text-sm flex items-center gap-2 transition-all hover:bg-slate-50 active:scale-95 shadow-sm"
                                onClick={prevStep}
                            >
                                <i className="fa-solid fa-chevron-left text-xs"></i>
                                Previous
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        <button 
                            type="button" 
                            onClick={handleSaveDraft}
                            disabled={submitting}
                            className="px-6 py-2.5 bg-white border border-gray-200 text-slate-700 rounded-lg text-sm font-semibold transition-all hover:bg-gray-50 flex items-center gap-2 active:scale-95 disabled:opacity-50 shadow-sm"
                        >
                            <i className="fa-solid fa-file-signature text-slate-500"></i> Save as Draft
                        </button>
                        
                        <button
                            type="button"
                            disabled={submitting}
                            className="px-6 py-2.5 rounded-lg bg-[#05144b] text-white font-semibold text-sm flex items-center gap-2 transition-all hover:bg-[#0a1e6e] active:scale-95 disabled:opacity-50 shadow-sm"
                            onClick={currentStep === 11 ? (e) => handleSubmit(e, 'Pending') : handleNextWithSave}
                        >
                            {submitting ? (
                                <>
                                    <i className="fa-solid fa-circle-notch animate-spin"></i>
                                    {currentStep === 11 ? 'Publishing...' : 'Saving...'}
                                </>
                            ) : (
                                <>
                                    {currentStep === 11 ? 'Publish Final Project' : `Save & Continue`}
                                    <i className="fa-solid fa-arrow-right text-xs"></i>
                                </>
                            )}
                        </button>
                    </div>
                </div>

            </form>

            {/* NEARBY PLACES MODAL */}
            {showNearbyModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
                    <div className="bg-white rounded-[2.5rem] w-full max-w-lg overflow-hidden shadow-2xl border border-black">
                        <div className="px-8 py-6 bg-[#05144b] text-white flex items-center justify-between">
                            <h3 className="text-xl font-bold">Add nearby place</h3>
                            <button onClick={() => setShowNearbyModal(false)} className="text-white transition-all"><i className="fa-solid fa-xmark text-xl"></i></button>
                        </div>
                        <div className="p-8 space-y-6">
                            <div className="space-y-2">
                                <label className="text-[12px] font-bold text-black ml-1">Select category</label>
                                <select 
                                    className="w-full px-5 py-4 rounded-xl bg-white border border-blue-900/10 text-black font-bold text-sm outline-none focus:border-[#05144b] appearance-none"
                                    value={nearbyEntry.category}
                                    onChange={(e) => setNearbyEntry({ ...nearbyEntry, category: e.target.value })}
                                >
                                    {(globalLandmarkCategories.length > 0 
                                        ? globalLandmarkCategories.map(c => c.name) 
                                        : Object.keys(NEARBY_CATEGORY_ICONS)
                                    ).map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[12px] font-bold text-black ml-1">Place name</label>
                                <input 
                                    type="text" 
                                    className="w-full px-5 py-4 rounded-xl bg-white border border-blue-900/10 text-black font-bold text-sm outline-none focus:border-[#05144b]"
                                    placeholder="Enter school or hospital name..."
                                    value={nearbyEntry.name}
                                    onChange={(e) => setNearbyEntry({ ...nearbyEntry, name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[12px] font-bold text-black ml-1">Distance (in KM)</label>
                                <div className="relative">
                                    <input 
                                        type="number" 
                                        step="0.1"
                                        className="w-full px-5 py-4 rounded-xl bg-white border border-blue-900/10 text-black font-bold text-sm outline-none focus:border-[#05144b]"
                                        placeholder="0.5"
                                        value={nearbyEntry.distance}
                                        onChange={(e) => setNearbyEntry({ ...nearbyEntry, distance: e.target.value })}
                                    />
                                    <span className="absolute right-5 top-1/2 -translate-y-1/2 text-[10px] font-black text-black opacity-40">KM</span>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mt-4">
                                <button 
                                    type="button"
                                    className="w-full py-5 bg-white border border-blue-900/10 text-black rounded-2xl font-bold text-xs active:scale-95 transition-all shadow-sm"
                                    onClick={() => setShowNearbyModal(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button"
                                    className="w-full py-5 bg-white border-2 border-[#05144b] text-[#05144b] rounded-2xl font-bold text-xs ] active:scale-95 transition-all shadow-md"
                                    onClick={() => {
                                        if(!nearbyEntry.name || !nearbyEntry.distance) {
                                            toast.error("Please fill all fields");
                                            return;
                                        }
                                        setForm({ ...form, nearbyPlaces: [...form.nearbyPlaces, nearbyEntry] });
                                        setNearbyEntry({ category: 'Schools', name: '', distance: '' });
                                        setShowNearbyModal(false);
                                    }}
                                >
                                    Save place
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}


